import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useEmployeeApprover } from "../../contexts/EmployeeApproverContext";
import Modal from "../../components/UI/Modal";
import EmployeeApproverForm from "../../components/Form/EmployeeApproverForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import { MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import ReactPaginate from 'react-paginate';
import StatusBadge from '../../components/UI/StatusBadge';


// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteEmployeeApprover }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteEmployeeApprover(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const EmployeeApproverMaster = React.memo(() => {
  const { employeeApprovers, fetchEmployeeApprovers, addEmployeeApprover, updateEmployeeApprover, deleteEmployeeApprover, isLoading, pagination } = useEmployeeApprover();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployeeApprover, setEditingEmployeeApprover] = useState(null);
  const [message, setMessage] = useState(""); 
  const [formErrors, setFormErrors] = useState({}); 
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    setIsClient(true); 
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingEmployeeApprover(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((employeeApprover) => {
    setEditingEmployeeApprover(employeeApprover);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingEmployeeApprover(null);
    setFormErrors({});
    setMessage("");
  }, []);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchEmployeeApprovers(newPage);
    }
  };

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (employeeApprover) => {
      setLoading(true);
      try {
        if (editingEmployeeApprover) {
          // console.log("department payload", employeeApprover);
        
          // Convert FormData to object
          // console.log(employeeApprover instanceof FormData)
          const employeeApproverData = employeeApprover instanceof FormData ? convertFormDataToObject(employeeApprover) : employeeApprover;
  
          // console.log("Payload being sent:", { ...editingEmployeeApprover, ...employeeApproverData });
  
          await updateEmployeeApprover({ ...editingEmployeeApprover, ...employeeApproverData });
          setMessage("Employee Approver updated successfully.");
          // setNotification("Employee Approver updated successfully.");
          setNotification({
            message: "Employee Approver updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create approver module", employeeApprover)
          await addEmployeeApprover(employeeApprover);
          setMessage("Employee Approver added successfully.");
          // setNotification("Employee Approver added successfully.");
          setNotification({
            message: "Employee Approver added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingEmployeeApprover, addEmployeeApprover, updateEmployeeApprover, closeModal]
  );

  // Memoize candidates with campaign names for the table
  const employeeApproverModuleForTable = useMemo(
    () =>
      employeeApprovers.map((employeeApprover) => ({
        ...employeeApprover,
        name: employeeApprover?.name || "Unknown Employee Approver",
      })),
    [employeeApprovers]
  );

  const handleDeleteEmployeeApprover = async (id) => {
    try {
      await deleteEmployeeApprover(id);
      // setNotification("Employee Approver deleted successfully");
      setNotification({
        message: "Employee Approver deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete employee approver");
      setNotification({
        message: "Failed to delete employee approver",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "EMP ID", field: "employee_id" },
      { label: "Approval Level", field: "approval_level_id" },
      { label: "Primary Approver", field: "primary_approver_id" },
      { label: "Secondary Approver", field: "secondary_approver_id" },
      { label: "Status", field: "is_active",
        render: (row) => (
          <StatusBadge status={row?.is_active} />
        ),
       },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteEmployeeApprover={handleDeleteEmployeeApprover}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteEmployeeApprover,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="text-2xl font-semibold text-black dark:text-white">Employee Approver Master</h2>
            <div className="flex gap-2"> 
            <button
                onClick={openModalForAdd}
                className="bg-blue-600 text-white px-4 py-2 font-medium rounded-md hover:bg-blue-700"
            >
                Add Employee Approver
            </button>
            </div> 
        </div>

        {/* Success Message */}
        {message && (
            <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
            {message}
            </div>
        )}

        {/* Skeleton Loader */}
        {loading ? (
            <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} height={40} />
            ))}
            </div>
        ) : (
            <div>
            <ReusableTable
                data={employeeApproverModuleForTable}
                // data={requests.slice().reverse()}
                loading={isLoading}
                columns={columns}
                filterable={true}
                excludeFilterColumns={["actions"]}
                // excludeFilterColumns={excludeFilterColumns}
            />
            </div>
        )}

        {/* Pagination */}
        {!isFiltering && employeeApprovers?.length > 0 && (
          // <ReactPaginate
          //     breakLabel="..."
          //     onPageChange={(event) => handlePageChange(event.selected + 1)}
          //     forcePage={currentPage - 1}
          //     pageRangeDisplayed={5}
          //     pageCount={pagination.totalPages}
          //     renderOnZeroPageCount={null}
          //     nextLabel="Next >"
          //     previousLabel="< Prev"
          //     previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          //     nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          //     containerClassName="flex items-center justify-center mt-4"
          //     pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
          //     activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
          // />

          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}


      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingEmployeeApprover ? "Edit Employee Approver" : "Add Employee Approver"}
      >
        <EmployeeApproverForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingEmployeeApprover || { employee_id: "", module_id: "", level_id: "", stage: "", primary_approver_id: "", secondary_approver_id: "", effective_from: "", effective_to: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default EmployeeApproverMaster;
